import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@kroll-resolver-crisp/ui-components-react';
import {formatNumber} from './format';
import {KPI_STATUS_STYLE, kpiStatus} from './kpiStatus';
import {type ReportKpiBlock, type ReportKpiGridBlock} from '@/api/reporting';
import {cn} from '@/lib/utils';

// The same tile the dashboard's queue totals use, so a KPI reads the same on both pages.
export function KpiBlock({block}: {readonly block: ReportKpiBlock | ReportKpiGridBlock}) {
  return 'metrics' in block ? <KpiGrid block={block} /> : <SingleKpi block={block} />;
}

function SingleKpi({block}: {readonly block: ReportKpiBlock}) {
  const status = kpiStatus(block.value, block.thresholds);
  const style = status === null ? null : KPI_STATUS_STYLE[status];

  return (
    <Card
      size="sm"
      data-block-id={block.id}
      data-block-type={block.type}
      data-status={status ?? undefined}
      className={cn(style && 'border-l-4', style?.edge)}>
      <CardHeader>
        <CardDescription>{block.title}</CardDescription>
        <CardTitle className="text-2xl font-semibold tracking-tight tabular-nums">
          {formatNumber(block.value, block.format)}
        </CardTitle>
        {style ? (
          <span
            className={cn('w-fit px-1.5 py-0.5 text-xs font-medium', style.chip)}
            data-testid="kpi-status">
            {style.label}
          </span>
        ) : null}
      </CardHeader>
      {block.caption ? (
        <CardContent className="text-muted-foreground">{block.caption}</CardContent>
      ) : null}
    </Card>
  );
}

// Several columns of one row as a grid of tiles in one card, laid out like the dashboard's target
// cards: the grid's own fill shows through the 1px gaps as the hairlines between tiles, so it is the
// border colour and the tiles sit on the page surface. An odd last tile takes the full row.
function KpiGrid({block}: {readonly block: ReportKpiGridBlock}) {
  return (
    <Card size="sm" data-block-id={block.id} data-block-type={block.type}>
      <CardHeader>
        <CardTitle>{block.title}</CardTitle>
        {block.description ? <CardDescription>{block.description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="bg-border grid grid-cols-2 gap-px p-px">
          {block.metrics.map((metric, i) => {
            const status = kpiStatus(metric.value, metric.thresholds);
            const style = status === null ? null : KPI_STATUS_STYLE[status];
            return (
              <div
                key={metric.field}
                data-metric={metric.field}
                data-status={status ?? undefined}
                className={cn(
                  'bg-card min-w-0 px-2 py-1.5',
                  style && 'border-l-4',
                  style?.edge,
                  i === block.metrics.length - 1 && block.metrics.length % 2 === 1 && 'col-span-2'
                )}>
                <p className="truncate text-base font-semibold tracking-tight tabular-nums">
                  {formatNumber(metric.value, metric.format)}
                </p>
                <p className="text-muted-foreground flex items-center gap-1.5 text-[10px] tracking-widest uppercase">
                  <span className="truncate">{metric.label}</span>
                  {style ? (
                    <span
                      className={cn('shrink-0 px-1 tracking-normal normal-case', style.chip)}
                      data-testid="kpi-status">
                      {style.label}
                    </span>
                  ) : null}
                </p>
              </div>
            );
          })}
        </div>
        {block.caption ? <p className="text-muted-foreground text-xs">{block.caption}</p> : null}
      </CardContent>
    </Card>
  );
}
